import { Table } from '@omnipresentgroup/design-system';
import { useCompensationVariantPayments } from '@omnipresentgroup/domains';
import type {
  CompensationSource,
  CompensationVariantPayment,
} from '@omnipresentgroup/types';
import {
  type ColumnDef,
  type ColumnOrderState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactElement, useEffect, useMemo, useState } from 'react';

import { useVariantPaymentsTableConfig } from './hooks';
import { ColumnHeaders, type EmployeeData } from './types';

const EMPTY_ARRAY: CompensationVariantPayment[] = [];

const LOADING_ARRAY = Array.from(
  { length: 5 },
  () => ({}) as CompensationVariantPayment,
);

type Props = {
  employeeData?: EmployeeData;
  source?: CompensationSource;
  optionsOverride?: Omit<
    TableOptions<CompensationVariantPayment>,
    'data' | 'columns'
  >;
  columnVisibility: Partial<{
    [key in keyof typeof ColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof ColumnHeaders)[];
  columnsOverride?: ColumnDef<CompensationVariantPayment, unknown>[];
  onRowClick?: (row: CompensationVariantPayment) => void;
  filterSection?: ReactElement;
};

export const VariantPaymentsList = ({
  employeeData,
  source,
  optionsOverride,
  columnVisibility,
  columnOrderOverride,
  onRowClick,
  filterSection,
}: Props) => {
  const [columnVisibilityState, setColumnVisibilityState] = useState<
    Props['columnVisibility']
  >({
    // Default all columns to false visibility
    ...Object.fromEntries(
      Object.keys(ColumnHeaders).map((current) => [current, false]),
    ),
    // Enable columns from consumer
    ...columnVisibility,
  });

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columnOrderOverride || [],
  );

  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    columnVisibility &&
      setColumnVisibilityState({
        // Default all columns to false visibility
        ...Object.fromEntries(
          Object.keys(ColumnHeaders).map((current) => [current, false]),
        ),
        // Enable columns from consumer
        ...columnVisibility,
      });
  }, [columnVisibility]);

  const {
    data: variantPayments,
    isLoading: isLoadingVariantPayments,
    isError,
  } = useCompensationVariantPayments({
    employeeId: employeeData?.id,
    source,
  });

  const { columns } = useVariantPaymentsTableConfig();

  const tableOptions: Omit<
    TableOptions<CompensationVariantPayment>,
    'data' | 'columns'
  > = useMemo(
    () => ({
      state: {
        columnVisibility: columnVisibilityState,
        columnOrder,
        sorting,
      },
      getCoreRowModel: getCoreRowModel(),
      onColumnVisibilityChange: setColumnVisibilityState,
      onColumnOrderChange: setColumnOrder,
      getPaginationRowModel: getPaginationRowModel(),
      onSortingChange: setSorting,
      getSortedRowModel: getSortedRowModel(),
      ...optionsOverride,
    }),
    [columnOrder, columnVisibilityState, optionsOverride, sorting],
  );

  const isLoading = useMemo(
    () => isLoadingVariantPayments,
    [isLoadingVariantPayments],
  );

  const table = useReactTable({
    data: isLoading ? LOADING_ARRAY : variantPayments || EMPTY_ARRAY,
    columns: columns,
    ...tableOptions,
  });

  return (
    <Table
      id="variant-payments-list"
      table={table}
      loading={isLoading}
      emptyStateProps={{
        id: 'variant-payments-empty-list',
        title: 'Nothing has been added yet',
      }}
      filterSection={filterSection}
      usePagination
      useSorting
      onRowClick={
        onRowClick
          ? (row: CompensationVariantPayment) => {
              !isError && onRowClick(row);
            }
          : undefined
      }
    />
  );
};
